import { useNavigate, useSearch } from "@tanstack/react-router"
import type { ExpandedState } from "@tanstack/react-table"
import { type RefObject, useCallback, useEffect, useRef, useState } from "react"
import {
  PRODUCT_TEST_RESULTS_ROUTE_LAYOUT_PATH,
  PRODUCT_TEST_RESULTS_ROUTE_PATH,
} from "routes/app/product/$productId/edit/_layout/quality/_layout/results.route"

import { useProductTestSearchStore } from "../stores/useProductTestSearchStore"

import type { ProductTest } from "@/graphql/codegen/graphql"
import { useScrollLoadMore } from "@/hooks/useScrollLoadMore"
import { useThrottle } from "@/hooks/useThrottle"
import { queryClient } from "@/providers/GraphqlRouterProvider"

interface UseProductTestsTableReturn {
  productTests: ProductTest[]
  isFetching: boolean
  error: string | null
  searchQuery: string | undefined
  tableContainerRef: RefObject<HTMLDivElement>
  expandedRow: ExpandedState
  refetch: () => void
  setExpandedRow: (updater: ExpandedState | ((old: ExpandedState) => ExpandedState)) => void
  handleSearchChange: (value: string) => void
  handleViewHistory: (productTest: ProductTest) => void
  handleScroll: () => void
}

export const useProductTestsTable = (productId: number): UseProductTestsTableReturn => {
  const navigate = useNavigate()
  const searchParams = useSearch({ from: PRODUCT_TEST_RESULTS_ROUTE_LAYOUT_PATH })
  const [includeHistory, setIncludeHistory] = useState<string[]>([])

  const [expandedRow, setExpandedRow] = useState<ExpandedState>({})
  const tableContainerRef = useRef<HTMLDivElement>(null)

  const handleSearchChange = useCallback(
    (value: string) => {
      navigate({
        search: { ...searchParams, query: value || undefined },
        from: PRODUCT_TEST_RESULTS_ROUTE_PATH,
        replace: true,
      })
    },
    [navigate, searchParams]
  )

  const handleViewHistory = (productTest: ProductTest) => {
    setIncludeHistory((prev) => {
      if (prev.includes(productTest.productTestId)) {
        return prev.filter((id) => id !== productTest.productTestId)
      }
      return [...prev, productTest.productTestId]
    })
  }

  const searchQuery = searchParams.query

  const data = useProductTestSearchStore(({ data }) => data)
  const fetching = useProductTestSearchStore(({ fetching }) => fetching)
  const error = useProductTestSearchStore(({ error }) => error)
  const executeSearch = useProductTestSearchStore(({ executeSearch }) => executeSearch)
  const loadMoreData = useProductTestSearchStore(({ loadMore }) => loadMore)

  const refetch = useCallback(() => {
    executeSearch(queryClient, { productId, includeHistory, query: searchQuery }, true)
    setExpandedRow({})
  }, [executeSearch, productId, includeHistory, searchQuery])

  const loadMore = useCallback(async () => {
    await loadMoreData(queryClient, { productId, includeHistory, query: searchQuery })
  }, [loadMoreData, productId, includeHistory, searchQuery])

  const [throttledLoadMore] = useThrottle(loadMore, 200)
  const handleScroll = useScrollLoadMore({
    fetching,
    loadMore: throttledLoadMore,
    scrollContainerRef: tableContainerRef,
  })

  useEffect(() => {
    executeSearch(queryClient, { productId, includeHistory, query: searchQuery })
    setExpandedRow({})
  }, [executeSearch, productId, includeHistory, searchQuery])

  const autoExpandedProductTestIdRef = useRef<string | null>(null)

  useEffect(() => {
    if (!searchQuery || !data.length || fetching) {
      autoExpandedProductTestIdRef.current = null
      return
    }

    const matchingProductTest = data.find((productTest) => productTest.lotNumber === searchQuery)

    if (!matchingProductTest || autoExpandedProductTestIdRef.current === matchingProductTest.productTestId) return

    autoExpandedProductTestIdRef.current = matchingProductTest.productTestId
    setExpandedRow((prevExpandedState) =>
      typeof prevExpandedState === "boolean"
        ? prevExpandedState
        : { ...prevExpandedState, [matchingProductTest.productTestId]: true }
    )
  }, [data, searchQuery, fetching])

  return {
    productTests: data,
    isFetching: fetching,
    error: error?.message || null,
    tableContainerRef,
    expandedRow,
    searchQuery,
    refetch,
    setExpandedRow,
    handleSearchChange,
    handleViewHistory,
    handleScroll,
  }
}
